import { z } from 'zod';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or non-negative number on the wire (matches the convention used in
// ar.ts / ap.ts / lva.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// Signed counterpart — for a cash-count difference (counted − expected) that goes
// NEGATIVE on a shortage. MoneySchema rejects negative strings, so the negative
// wire value lands on the z.coerce.number() branch. Same inferred type.
const SignedDecimalLike = z.union([MoneySchema, z.coerce.number()]);
// ============================================================
// Legacy expense-category hint list (prototype §24). NOTE: post-COA migration
// these 4-digit codes no longer exist in the real chart of accounts and are
// NOT a validation gate anymore — the API validates categoryCode against the
// company COA (postable, active, COST/EXPENSE). This list is retained ONLY as
// a UI fallback/dropdown hint for the web client; do not use it to gate writes.
// ============================================================
// Phase 12: renumbered 52xx → 62xx (EXPENSE moved out of 5xxx range).
export const PETTY_CASH_CATEGORY_CODES = [
    '6210', // ค่าน้ำมันเชื้อเพลิง
    '6211', // ค่าทางด่วน / ที่จอดรถ
    '6212', // ค่ารับรอง / เลี้ยงลูกค้า
    '6213', // ค่าวัสดุสำนักงาน
    '6214', // ค่าขนส่งย่อย
    '6215', // ค่าซ่อมแซมเล็กน้อย
    '6216', // ค่าโทรศัพท์ / อินเทอร์เน็ต
    '6217', // ค่าบริการอื่นๆ
    '6218', // ค่าเลี้ยงรับรองพนักงาน
    '6219', // ค่าใช้จ่ายเบ็ดเตล็ด
];
export const PettyCashCategoryCodeSchema = z.enum(PETTY_CASH_CATEGORY_CODES);
// ============================================================
// Enums
// ============================================================
export const PettyCashFloatStatusSchema = z.enum(['ACTIVE', 'CLOSED']);
export const PettyCashEntryStatusSchema = z.enum([
    'POSTED',
    'REPLENISHED',
    'PENDING_APPROVAL',
    'REJECTED',
]);
// ============================================================
// PettyCashFloat — open float per (company, branch)
// ============================================================
export const PettyCashFloatSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    branchId: z.uuid(),
    custodianId: z.uuid().nullable().optional(),
    floatAmount: DecimalLike,
    currentBalance: DecimalLike,
    openedDate: z.coerce.date(),
    status: PettyCashFloatStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    txnApprovalThreshold: DecimalLike.nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row shape — adds branch name + custodian name for table rendering.
export const PettyCashFloatListRowSchema = PettyCashFloatSchema.extend({
    branchName: z.string(),
    branchCode: z.string().nullable().optional(),
    custodianName: z.string().nullable().optional(),
});
// Wire shape for POST — voucherNo / currentBalance / status are server-derived.
// fromAccountCode lets the caller pick which bank funds the float (defaults to
// the company's BANK_PRIMARY mapping if omitted in the service).
export const CreatePettyCashFloatSchema = z.object({
    branchId: z.uuid(),
    floatAmount: z.coerce.number().positive(),
    openedDate: z.iso.date(),
    fromAccountCode: z.string().min(1).optional(),
    custodianId: z.uuid().optional(),
    txnApprovalThreshold: z.coerce.number().positive().optional(),
});
// ============================================================
// PettyCashEntry — single expense paid from the float
// ============================================================
export const PettyCashEntrySchema = z.object({
    id: z.uuid(),
    floatId: z.uuid(),
    voucherNo: z.string().min(1),
    entryDate: z.coerce.date(),
    description: z.string().min(1),
    categoryCode: z.string().min(1),
    departmentId: z.uuid().nullable().optional(),
    amount: DecimalLike,
    payeeName: z.string().nullable().optional(),
    vendorTaxId: z.string().nullable().optional(),
    receiptNo: z.string().nullable().optional(),
    isNoDoc: z.boolean().optional(),
    hasReceipt: z.boolean().optional(),
    status: PettyCashEntryStatusSchema,
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.number().int().optional(),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    approvals: z
        .array(z.object({
        stepNo: z.number().int(),
        roleCode: z.string(),
        roleLabel: z.string(),
        approvedAt: z.coerce.date().nullable(),
        approvedBy: z.string().nullable(),
        comment: z.string().nullable(),
    }))
        .optional(),
    journalEntryId: z.uuid().nullable().optional(),
    replenishmentId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    // M4a — input-VAT / WHT display fields (entity-side, so the web can render the
    // tax breakdown on a posted entry). vatTreatment routes the Dr input-VAT leg
    // (CLAIMABLE|SUSPENSE|PROHIBITED); vatRealizedAt stamps when a SUSPENSE entry
    // was later realized; whtCertificateId links the minted 50-ทวิ certificate.
    vatTreatment: z.string().nullable().optional(),
    vatRate: DecimalLike.nullable().optional(),
    vatAmount: DecimalLike.nullable().optional(),
    taxInvoiceNo: z.string().nullable().optional(),
    taxInvoiceDate: z.coerce.date().nullable().optional(),
    vatRealizedAt: z.coerce.date().nullable().optional(),
    whtRate: DecimalLike.nullable().optional(),
    whtAmount: DecimalLike.nullable().optional(),
    whtIncomeType: z.string().nullable().optional(),
    whtCertificateId: z.uuid().nullable().optional(),
    // Client-supplied dedup key (#4). Server-set from the Idempotency-Key header;
    // surfaced on the entity for type-consistency, never accepted in the body.
    idempotencyKey: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============================================================
// PettyCashEntryApproval — per-step approval row (M3 maker-checker)
// ============================================================
export const PettyCashEntryApprovalSchema = z.object({
    id: z.uuid(),
    entryId: z.uuid(),
    stepNo: z.number().int(),
    roleCode: z.string(),
    roleLabel: z.string(),
    approvedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    comment: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
});
// ============================================================
// PettyCashAttachment — receipt / document attached to an entry
// ============================================================
export const PettyCashAttachmentSchema = z.object({
    id: z.uuid(),
    entryId: z.uuid().nullable().optional(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    caption: z.string().nullable().optional(),
    uploadedAt: z.coerce.date(),
    downloadUrl: z.string().optional(),
});
// Wire shape for POST — caller picks float + category + amount. voucherNo +
// JV are server-derived; the service validates amount <= currentBalance AND
// that categoryCode is a postable COST/EXPENSE COA account for the company.
// categoryCode is a free COA code string here (not the legacy enum) so real
// 8-digit codes pass the wire; the COA lookup in the service is the real gate.
// M4a — input-VAT credit treatment for a petty-cash entry. CLAIMABLE = ภาษีซื้อ
// claimable now (Dr VAT_INPUT); SUSPENSE = ภาษีซื้อค้างรับ awaiting the original
// tax invoice (Dr VAT_INPUT_SUSPENSE, later realized); PROHIBITED = ภาษีซื้อ
// ต้องห้าม not claimable (Dr VAT_INPUT_PROHIBITED). Null/absent = no VAT line.
export const PettyCashVatTreatmentSchema = z.enum(['CLAIMABLE', 'SUSPENSE', 'PROHIBITED']);
export const CreatePettyCashEntrySchema = z.object({
    floatId: z.uuid(),
    entryDate: z.iso.date(),
    description: z.string().min(1),
    categoryCode: z.string().min(1),
    departmentId: z.uuid().nullable().optional(),
    amount: z.coerce.number().positive(),
    payeeName: z.string().min(1).max(255).optional(),
    // #21: a Thai tax ID is exactly 13 digits. Optional — absent is fine; when
    // provided it must be 13 digits (not any 1–13 char string).
    vendorTaxId: z
        .string()
        .regex(/^\d{13}$/, 'เลขประจำตัวผู้เสียภาษีต้องเป็นตัวเลข 13 หลัก')
        .optional(),
    receiptNo: z.string().min(1).max(60).optional(),
    attachmentId: z.uuid().optional(),
    isNoDoc: z.coerce.boolean().optional(),
    // M4a — Thai input VAT (the entry `amount` stays the GROSS, VAT-inclusive
    // expense). vatTreatment routes the Dr input-VAT leg; vatAmount is the VAT
    // baht carved out of the gross (expense is booked NET of it). taxInvoiceNo /
    // taxInvoiceDate are the supporting ใบกำกับภาษี metadata.
    vatTreatment: PettyCashVatTreatmentSchema.optional(),
    vatRate: z.coerce.number().nonnegative().optional(),
    vatAmount: z.coerce.number().nonnegative().optional(),
    taxInvoiceNo: z.string().min(1).max(60).optional(),
    taxInvoiceDate: z.iso.date().optional(),
    // M4a — WHT withheld at the petty-cash payout. whtAmount reduces the cash that
    // actually leaves the tin and books a Cr WHT_PAYABLE leg; whtIncomeType selects
    // the ภ.ง.ด. income type for the 50-ทวิ certificate minted at post time.
    whtRate: z.coerce.number().nonnegative().optional(),
    whtAmount: z.coerce.number().nonnegative().optional(),
    whtIncomeType: z.string().min(1).max(40).optional(),
});
// ============================================================
// PettyCashReplenishment — refill voucher
// ============================================================
export const PettyCashReplenishmentSchema = z.object({
    id: z.uuid(),
    floatId: z.uuid(),
    voucherNo: z.string().min(1),
    replenishDate: z.coerce.date(),
    amount: DecimalLike,
    fromAccountCode: z.string().min(1),
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Detail shape includes the rolled-up entries for the audit trail.
export const PettyCashReplenishmentWithEntriesSchema = PettyCashReplenishmentSchema.extend({
    entries: z.array(PettyCashEntrySchema),
});
// Wire shape for POST — caller passes the float + bank. entryIds optional:
// when omitted, the service pulls all POSTED entries for the float (typical
// "refill everything outstanding" workflow). When supplied, only those entry
// IDs are rolled into the refill (selective replenishment).
export const CreatePettyCashReplenishmentSchema = z.object({
    floatId: z.uuid(),
    replenishDate: z.iso.date(),
    fromAccountCode: z.string().min(1),
    entryIds: z.array(z.uuid()).optional(),
});
// ============================================================
// Shared engine fields — canonical maker-checker columns + per-step approvals,
// identical to the shape carried on PettyCashEntrySchema (M3). Reused by the
// cash-count (PCC) and float-close (PCX) documents below.
// ============================================================
const pettyCashEngineFields = {
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.number().int().optional(),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    approvals: z
        .array(z.object({
        stepNo: z.number().int(),
        roleCode: z.string(),
        roleLabel: z.string(),
        approvedAt: z.coerce.date().nullable(),
        approvedBy: z.string().nullable(),
        comment: z.string().nullable(),
    }))
        .optional(),
};
export const PettyCashCountCloseStatusSchema = z.enum(['PENDING_APPROVAL', 'POSTED', 'REJECTED']);
// ============================================================
// PettyCashCount — periodic cash count (PCC). difference = counted − expected;
// approval posts an over/short JV through the engine (1 approver, FINANCE_MANAGER).
// ============================================================
export const PettyCashCountSchema = z.object({
    id: z.uuid(),
    floatId: z.uuid(),
    voucherNo: z.string().min(1),
    countDate: z.coerce.date(),
    expectedBalance: DecimalLike,
    countedBalance: DecimalLike,
    difference: SignedDecimalLike,
    reason: z.string().nullable().optional(),
    countedBy: z.string().nullable().optional(),
    status: PettyCashCountCloseStatusSchema,
    ...pettyCashEngineFields,
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Wire shape for POST — voucherNo / expectedBalance / difference are
// server-derived (expected = the float's currentBalance at count time).
export const CreatePettyCashCountSchema = z.object({
    floatId: z.uuid(),
    countDate: z.iso.date(),
    countedBalance: z.coerce.number(),
    reason: z.string().optional(),
});
// ============================================================
// PettyCashClose — float close-out (PCX). Returns currentBalance to a bank
// account and marks the float CLOSED; runs through the engine (2 steps,
// FINANCE_MANAGER → CFO).
// ============================================================
export const PettyCashCloseSchema = z.object({
    id: z.uuid(),
    floatId: z.uuid(),
    voucherNo: z.string().min(1),
    closeDate: z.coerce.date(),
    returnedAmount: DecimalLike,
    toAccountCode: z.string().min(1),
    reason: z.string().nullable().optional(),
    custodyNotes: z.string().nullable().optional(),
    closedBy: z.string().nullable().optional(),
    status: PettyCashCountCloseStatusSchema,
    ...pettyCashEngineFields,
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Wire shape for POST — voucherNo / JV are server-derived; toAccountCode is the
// bank the residual cash is returned to.
export const CreatePettyCashCloseSchema = z.object({
    floatId: z.uuid(),
    closeDate: z.iso.date(),
    returnedAmount: z.coerce.number().nonnegative(),
    toAccountCode: z.string().min(1),
    reason: z.string().optional(),
    custodyNotes: z.string().optional(),
});
//# sourceMappingURL=petty-cash.js.map